// Split out of journey.ts (ADR-002 phase C): share-permissions modal,
// export and in-journey search.

import { $ } from '../shared/dom';
import { showToast } from '../shared/ui';
import { apiFetch, apiFetchBlob } from '../api/client';
import { trajState, escapeHtml } from './journey-state';
import { openObservationDrawer } from './journey-drawer';
import type { JourneyCategory, JourneyElement } from './journey.types';

export function initJourneySharing() {
  // --- Модалка шеринга ---
  const SPHERE_LABELS: Record<JourneyCategory, string> = {
    work: 'Работа',
    relation: 'Отношения',
    project: 'Проекты',
  };

  function renderShareScopeOptions() {
    const container = $('share-scopes-list');
    if (!container) return;
    const elements = trajState.currentJourney?.elements || [];
    if (elements.length === 0) {
      container.innerHTML = '<div class="share-scopes-hint">Сначала создайте элементы в карте истории.</div>';
      return;
    }
    container.innerHTML = elements
      .map(
        (el) => `
        <label class="share-scope-item">
          <input type="checkbox" class="share-scope-checkbox" value="${escapeHtml(el.id)}" data-title="${escapeHtml(el.title)}">
          <span>${escapeHtml(SPHERE_LABELS[el.category] || el.category)} — ${escapeHtml(el.title)}</span>
        </label>`
      )
      .join('');
  }

  function getSelectedShareScopes(): Array<{ scope_type: 'element'; scope_id: string }> {
    const checked = document.querySelectorAll<HTMLInputElement>('.share-scope-checkbox:checked');
    return Array.from(checked).map((input) => ({
      scope_type: 'element' as const,
      scope_id: input.value,
    }));
  }

  function formatPermissionScopes(
    scopes: Array<{ scope_type: string; scope_id: string }> | undefined,
    elements: JourneyElement[] | undefined
  ): string {
    return formatPermissionScopeLabel(scopes, elements);
  }

  $('btn-share-journey')?.addEventListener('click', () => {
    const modal = $('journey-share-modal');
    if (modal) {
      modal.classList.remove('hidden');
      renderShareScopeOptions();
      loadSharePermissions();
    }
  });

  const closeShare = () => {
    $('journey-share-modal')?.classList.add('hidden');
  };
  $('btn-close-share-modal')?.addEventListener('click', closeShare);
  $('journey-share-backdrop')?.addEventListener('click', closeShare);

  $('share-add-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!trajState.currentJourney) return;
    const usernameInput = $<HTMLInputElement>('share-username-input');
    const roleSelect = $<HTMLSelectElement>('share-role-select');
    if (!usernameInput || !roleSelect) return;

    const targetUsername = usernameInput.value.trim();
    const role = roleSelect.value;
    if (!targetUsername) return;

    const scopes = getSelectedShareScopes();
    const payload: Record<string, unknown> = { targetUsername, role };
    if (scopes.length > 0) payload.scopes = scopes;

    try {
      await apiFetch(`/api/journeys/${trajState.currentJourney.id}/permissions`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      showToast('Доступ успешно предоставлен');
      usernameInput.value = '';
      document.querySelectorAll<HTMLInputElement>('.share-scope-checkbox:checked').forEach((cb) => {
        cb.checked = false;
      });
      await loadSharePermissions();
    } catch (err: any) {
      showToast(err.message || 'Ошибка предоставления доступа', 'error');
    }
  });

  // Автозаполнение списка пользователей при вводе
  const shareUsernameInput = $<HTMLInputElement>('share-username-input');
  const shareDatalist = $('share-users-datalist');
  if (shareUsernameInput && shareDatalist) {
    let debounceTimer: any = null;
    shareUsernameInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        const val = shareUsernameInput.value.trim();
        if (val.length < 1) {
          shareDatalist.innerHTML = '';
          return;
        }
        try {
          const matchedUsers = await apiFetch<string[]>(`/api/users/search?q=${encodeURIComponent(val)}`);
          shareDatalist.innerHTML = matchedUsers
            .map(u => `<option value="${escapeHtml(u)}"></option>`)
            .join('');
        } catch (err) {
          console.error('Failed to search users', err);
        }
      }, 200);
    });
  }

  // Экспорт и поиск
  $('btn-export-json')?.addEventListener('click', () => exportJourneyData('json'));
  $('btn-export-csv')?.addEventListener('click', () => exportJourneyData('csv'));
  
  $('journey-search-input')?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runJourneySearch();
    }
  });
}

const PERMISSION_SPHERE_LABELS: Record<JourneyCategory, string> = {
  work: 'Работа',
  relation: 'Отношения',
  project: 'Проекты',
};

function formatPermissionScopeLabel(
  scopes: Array<{ scope_type: string; scope_id: string }> | undefined,
  elements: JourneyElement[] | undefined
): string {
  if (!scopes || scopes.length === 0) return 'Вся карта';
  const byId = new Map((elements || []).map((el) => [el.id, el]));
  const labels = scopes
    .filter((s) => s.scope_type === 'element')
    .map((s) => {
      const el = byId.get(s.scope_id);
      if (!el) return 'Сфера';
      return `${PERMISSION_SPHERE_LABELS[el.category] || el.category}: ${el.title}`;
    });
  return labels.length > 0 ? labels.join(', ') : 'Ограниченный доступ';
}

async function loadSharePermissions() {
  if (!trajState.currentJourney) return;
  try {
    const list = await apiFetch<any[]>(`/api/journeys/${trajState.currentJourney.id}/permissions`);
    const container = $('share-users-list');
    if (!container) return;
    container.innerHTML = '';
    
    const filtered = list.filter(p => p.role !== 'owner');
    
    if (filtered.length === 0) {
      container.innerHTML = `<div style="font-size: 0.8rem; color: var(--muted); font-style: italic; text-align: center; padding: 10px;">Никому еще не предоставлен доступ</div>`;
      return;
    }
    
    filtered.forEach(p => {
      const row = document.createElement('div');
      row.className = 'share-user-row';
      const scopeLabel = formatPermissionScopeLabel(p.scopes, trajState.currentJourney?.elements);
      row.innerHTML = `
        <div class="share-user-info">
          <span class="share-username">${escapeHtml(p.username)}</span>
          <span class="share-role">${p.role === 'editor' ? 'Редактор' : 'Читатель'}</span>
          <span class="share-user-scopes">${escapeHtml(scopeLabel)}</span>
        </div>
        <button class="btn-action-icon delete-perm-btn" data-id="${p.id}" title="Отозвать доступ">🗑️</button>
      `;
      row.querySelector('.delete-perm-btn')?.addEventListener('click', async () => {
        if (confirm(`Отозвать доступ для пользователя ${p.username}?`)) {
          await deletePermission(p.id);
        }
      });
      container.appendChild(row);
    });
  } catch (err: any) {
    console.error('Failed to load share permissions', err);
    showToast('Ошибка при загрузке прав доступа', 'error');
  }
}

async function deletePermission(permId: string) {
  try {
    await apiFetch(`/api/journey-permissions/${permId}`, { method: 'DELETE' });
    showToast('Доступ отозван');
    await loadSharePermissions();
  } catch (err: any) {
    showToast(err.message || 'Ошибка отзыва прав доступа', 'error');
  }
}

async function exportJourneyData(format: 'json' | 'csv') {
  if (!trajState.currentJourney) return;
  try {
    const response = await apiFetchBlob(`/api/journeys/${trajState.currentJourney.id}/export?format=${format}`);
    const disposition = response.headers.get('Content-Disposition') || '';
    let filename = `journey.${format}`;
    if (disposition.includes("filename*=UTF-8''")) {
      const parts = disposition.split("filename*=UTF-8''");
      if (parts.length > 1) {
        filename = decodeURIComponent(parts[1].trim());
      }
    } else {
      const match = disposition.match(/filename="([^"]+)"/);
      if (match) {
        filename = match[1];
      }
    }
    
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    showToast(`История успешно экспортирована в ${format.toUpperCase()}`);
  } catch (err: any) {
    console.error(`Failed to export journey in ${format}`, err);
    showToast(`Ошибка экспорта истории: ${err.message}`, 'error');
  }
}

async function runJourneySearch() {
  if (!trajState.currentJourney) return;
  const input = $<HTMLInputElement>('journey-search-input');
  const container = $('journey-search-results');
  if (!input || !container) return;
  
  const query = input.value.trim();
  if (!query) {
    container.innerHTML = '';
    container.classList.add('hidden');
    return;
  }
  
  container.innerHTML = '<div style="font-size: 0.8rem; color: var(--muted); padding: 4px;">Поиск...</div>';
  container.classList.remove('hidden');
  
  try {
    const results = await apiFetch<any[]>(`/api/journeys/${trajState.currentJourney.id}/search?query=${encodeURIComponent(query)}`) || [];
    
    if (results.length === 0) {
      container.innerHTML = '<div style="font-size: 0.8rem; color: var(--muted); padding: 4px;">Ничего не найдено</div>';
      return;
    }
    
    container.innerHTML = '';
    results.forEach(res => {
      const div = document.createElement('div');
      div.className = 'journey-search-result-item';
      div.style.cssText = 'padding: 8px; border-bottom: 1px solid var(--border); cursor: pointer; transition: background 0.2s; border-radius: var(--radius-sm); margin-bottom: 4px;';
      div.addEventListener('mouseover', () => div.style.background = 'var(--surface-hover)');
      div.addEventListener('mouseout', () => div.style.background = 'transparent');
      
      const typeLabels: Record<string, string> = { element: 'Сфера', observation: 'Наблюдение', cycle: 'Цикл' };
      const typeIcon = res.type === 'cycle' ? '📝' : res.type === 'observation' ? '👁️' : '💼';
      
      let path = `${res.sphere} • ${res.elementTitle}`;
      if (res.observationTitle) path += ` • ${res.observationTitle}`;
      
      div.innerHTML = `
        <div style="display: flex; justify-content: space-between; font-size: 0.75rem; color: var(--muted); margin-bottom: 2px;">
          <span>${typeIcon} ${typeLabels[res.type] || res.type}</span>
          <span>${path}</span>
        </div>
        <div style="font-size: 0.85rem; font-weight: 600; color: var(--text); margin-bottom: 2px;">${escapeHtml(res.title)}</div>
        <div style="font-size: 0.8rem; color: var(--muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${escapeHtml(res.text)}</div>
      `;
      
      div.addEventListener('click', () => {
        if (res.observationId) {
          openObservationDrawer(res.observationId);
          container.classList.add('hidden');
          input.value = '';
        } else {
          container.classList.add('hidden');
        }
      });
      
      container.appendChild(div);
    });
  } catch (err: any) {
    console.error('Search failed', err);
    container.innerHTML = `<div style="font-size: 0.8rem; color: var(--danger); padding: 4px;">Ошибка поиска: ${escapeHtml(err.message)}</div>`;
  }
}
